/**
 * Per-device config documents (plan 034).
 *
 * The PWA is where the merge happens. We hold the family defaults and the
 * per-device override map, resolve them here, and write one complete
 * `PlaintextConfig` per device. The endpoint then fetches exactly one
 * ciphertext and decrypts it the way it always has — no merge logic in the
 * binary, and none on the server, which holds no key.
 *
 * The cost is that a family-wide change is N writes. With a handful of
 * devices that is fine, and it buys the endpoint staying dumb.
 */

import { encrypt } from '../e2ee/envelope';
import { familyKeyInMemory } from '../state/session';
import { api } from '../state/api';
import {
  currentConfig,
  resolve,
  toBase64,
  type DeviceOverride,
  type PlaintextConfig,
} from './config';

/**
 * AAD binding a config envelope to one endpoint. Must match
 * `backstop_core::e2ee_ops::device_config_aad` byte for byte.
 *
 * The family envelope uses a fixed string, which was fine when every device
 * received identical ciphertext. Per-device documents need the binding:
 * without it nothing inside the envelope says who it was for, so a row swap
 * in `endpoint_configs` could move one child's settings onto another's and
 * the endpoint would decrypt it happily.
 *
 * `endpointId` must be the lowercase hyphenated form Rust's
 * `Uuid::to_string` produces. It arrives that way from Postgres via serde,
 * so the rule is simply: don't reformat it.
 */
export function deviceConfigAad(endpointId: string): Uint8Array {
  return new TextEncoder().encode(`endpoint-config:v1:${endpointId}`);
}

/** Outcome of one device's write, so callers can report partial failure. */
export interface DeviceWriteResult {
  endpointId: string;
  ok: boolean;
  error?: string;
}

/**
 * Encrypt and PUT one device's fully-resolved config.
 *
 * Note the body shape: this route takes an `EncryptedConfig` JSON envelope
 * with base64 `content_ct`, exactly like `PUT /v1/family/config`. It is NOT
 * a raw-bytes endpoint, so `apiPutBytes` would send something the endpoint
 * cannot decrypt — the same class of wire-format mismatch that once made
 * every config PUT store garbage.
 */
export async function saveDeviceConfig(
  endpointId: string,
  resolved: PlaintextConfig,
): Promise<void> {
  const fk = familyKeyInMemory.value;
  if (fk === null) throw new Error('no family key in memory');
  const ct = await encrypt(
    fk,
    new TextEncoder().encode(JSON.stringify(resolved)),
    deviceConfigAad(endpointId),
  );
  await api(`/v1/family/endpoints/${endpointId}/config`, {
    method: 'PUT',
    // `version` is ignored server-side (it assigns `existing + 1`); sent
    // only because the envelope type requires the field.
    body: JSON.stringify({ version: 1, content_ct: toBase64(ct) }),
  });
}

/**
 * Resolve and write the document for a single device, from the currently
 * loaded family config.
 */
export async function syncDeviceConfig(endpointId: string): Promise<void> {
  const base = currentConfig.value;
  if (base === null) throw new Error('config not loaded — call fetchLatest()');
  await saveDeviceConfig(
    endpointId,
    resolve(base, base.device_overrides[endpointId]),
  );
}

/**
 * Recompute and write every device's document. Called after a family-defaults
 * save, when a device's override changes, and by the dashboard's
 * config-on-sight loop for devices that have no document yet.
 *
 * `allSettled`, not `all`: one device's PUT failing must not lose the writes
 * that succeeded, nor make the parent believe nothing saved. Callers surface
 * the per-device results.
 */
export async function syncDeviceConfigs(
  endpointIds: string[],
): Promise<DeviceWriteResult[]> {
  const base = currentConfig.value;
  if (base === null) throw new Error('config not loaded — call fetchLatest()');

  const settled = await Promise.allSettled(
    endpointIds.map(async (id) => {
      await saveDeviceConfig(id, resolve(base, base.device_overrides[id]));
    }),
  );
  return endpointIds.map((endpointId, i) => {
    const outcome = settled[i];
    if (outcome === undefined || outcome.status === 'fulfilled') {
      return { endpointId, ok: true };
    }
    const reason: unknown = outcome.reason;
    return {
      endpointId,
      ok: false,
      error: reason instanceof Error ? reason.message : String(reason),
    };
  });
}

/**
 * Write `override` for one device, then push that device's document.
 *
 * Saves the family config too, because that is where the override map is
 * stored. Passing `undefined` clears the device's overrides — the "reset to
 * family defaults" action.
 */
export async function setDeviceOverride(
  endpointId: string,
  override: DeviceOverride | undefined,
  saveFamily: (next: PlaintextConfig) => Promise<void>,
): Promise<void> {
  const base = currentConfig.value;
  if (base === null) throw new Error('config not loaded — call fetchLatest()');

  // Drop the key entirely rather than storing `{}`. An empty object and a
  // missing one resolve identically, but a stored `{}` would make the device
  // read as "customised" everywhere we count keys.
  const { [endpointId]: _cleared, ...withoutDevice } = base.device_overrides;
  const nextOverrides: Record<string, DeviceOverride> =
    override === undefined || Object.keys(override).length === 0
      ? withoutDevice
      : { ...withoutDevice, [endpointId]: override };

  const nextFamily: PlaintextConfig = {
    ...base,
    device_overrides: nextOverrides,
  };
  await saveFamily(nextFamily);
  await saveDeviceConfig(
    endpointId,
    resolve(nextFamily, nextOverrides[endpointId]),
  );
}
